import { makeAutoObservable, runInAction } from "mobx";

import { StockistIssue } from "../../../Common/DTOs/StockistIssue";
import { issueAjaxController } from "../../../Common/Ajax/IssueAjaxController";

class IssueDetailsController {
  isAllIssuesLoading = false;
  isIssueLoading = false;
  issue: StockistIssue | null = null;
  allIssuesOrdered: StockistIssue[] = [];
  previousMagazineId: number | null = null;
  pendingMagazineId: number | null = null;
  magazineChanged = false;
  isMagazineCurrentIssueError = false;

  constructor() {
    makeAutoObservable(this);
  }

  get upcoming() {
    return this.allIssuesOrdered.filter(iss => iss.issueOnSale > new Date());
  }

  get current() {
    return this.allIssuesOrdered.filter(iss => iss.id === this.issue?.magazineCurrentIssueInfo?.id && iss.issueOnSale <= new Date());
  }

  get back() {
    return this.allIssuesOrdered.filter(iss => iss.id !== this.issue?.magazineCurrentIssueInfo?.id && iss.issueOnSale <= new Date());
  }

  setMagazineHint(magazineId: number) {
    this.pendingMagazineId = magazineId;
  }

  async loadIssue(id: number) {
    this.isIssueLoading = true;

    const hintMagazineId = this.pendingMagazineId;
    this.pendingMagazineId = null;

    if (hintMagazineId !== null) {
      const magazineChanged = this.previousMagazineId !== hintMagazineId;
      if (magazineChanged || this.allIssuesOrdered.length === 0) {
        void this.loadAllIssuesForMagazine(hintMagazineId);
      }
    }

    try {
      const result = await issueAjaxController.getIssueById(id);

      runInAction(() => {
        const newMagazineId = result.magazineId;
        const magazineChanged = this.previousMagazineId !== newMagazineId;

        this.issue = result;
        this.previousMagazineId = newMagazineId;
        this.magazineChanged = magazineChanged;

        if (hintMagazineId === null && (magazineChanged || this.allIssuesOrdered.length === 0))
          this.loadAllIssuesForMagazine(newMagazineId);
      });
    } catch (err) {
      if (err === "CANCELLED") return;
    } finally {
      runInAction(() => {
        this.isIssueLoading = false;
      });
    }
  }

  async loadAllIssuesForMagazine(magazineId: number) {
    this.isAllIssuesLoading = true;
    try {
      const res = await issueAjaxController.getAllIssuesForMagazine(magazineId);

      runInAction(() => this.allIssuesOrdered = res.map(iss => new StockistIssue(iss)));
      runInAction(() => this.isAllIssuesLoading = false);
    } catch (err) {
      if (err === "CANCELLED") return;
      runInAction(() => this.isAllIssuesLoading = false);
    }
  }

  async loadCurrentIssueIdByMagazineName(magazineSlug: string | undefined) {
    if (!magazineSlug) {
      this.isIssueLoading = false;
      this.isMagazineCurrentIssueError = true;
      return;
    }

    this.isMagazineCurrentIssueError = false;
    this.isIssueLoading = true;
    try {
      const currentIssueId = await issueAjaxController.getCurrentIssueIdByMagazineSlug(magazineSlug);

      if (!currentIssueId)
        runInAction(() => this.isMagazineCurrentIssueError = true);
      else
        await this.loadIssue(currentIssueId);
    } catch (err) {
      if (err === "CANCELLED") return;
    } finally {
      runInAction(() => this.isIssueLoading = false);
    }
  }

  clear() {
    this.issue = null;
    this.allIssuesOrdered = [];
    this.isIssueLoading = false;
    this.isAllIssuesLoading = false;
    this.previousMagazineId = null;
    this.pendingMagazineId = null;
    this.magazineChanged = false;
  }
}

export const issueDetailsController = new IssueDetailsController();
